import { Bars2Icon, Bars4Icon, ChevronUpDownIcon, PencilIcon } from '@heroicons/react/24/outline';

import { ICON_MAP } from 'components/icons';
import S from 'constants/string.const';

export const QUESTION_TYPES = {
  SHORT_ANSWER: 'short_answer',
  LONG_ANSWER: 'long_answer',
  YES_NO: 'yes_no',
  CHECKBOXES: 'checkboxes',
  RADIO_BUTTONS: 'radio_buttons',
  DROPDOWN: 'dropdown',
  SIGNATURE: 'signature',
};

export const QUESTION_TYPE_LABELS = {
  [QUESTION_TYPES.SHORT_ANSWER]: 'consultationForms.questionTypes.short_answer',
  [QUESTION_TYPES.LONG_ANSWER]: 'consultationForms.questionTypes.long_answer',
  [QUESTION_TYPES.YES_NO]: 'consultationForms.questionTypes.yes_no',
  [QUESTION_TYPES.CHECKBOXES]: 'consultationForms.questionTypes.checkboxes',
  [QUESTION_TYPES.RADIO_BUTTONS]: 'consultationForms.questionTypes.radio_buttons',
  [QUESTION_TYPES.DROPDOWN]: 'consultationForms.questionTypes.dropdown',
  [QUESTION_TYPES.SIGNATURE]: 'consultationForms.questionTypes.signature',
};

export const QUESTION_TYPE_ICON = {
  [QUESTION_TYPES.SHORT_ANSWER]: Bars2Icon,
  [QUESTION_TYPES.LONG_ANSWER]: Bars4Icon,
  [QUESTION_TYPES.YES_NO]: ICON_MAP['yes-no'],
  [QUESTION_TYPES.CHECKBOXES]: ICON_MAP['checkmark-square'],
  [QUESTION_TYPES.RADIO_BUTTONS]: ICON_MAP['radio-button-checked'],
  [QUESTION_TYPES.DROPDOWN]: ChevronUpDownIcon,
  [QUESTION_TYPES.SIGNATURE]: PencilIcon,
};

export const MULTI_OPTION_TYPES = [QUESTION_TYPES.CHECKBOXES, QUESTION_TYPES.RADIO_BUTTONS, QUESTION_TYPES.DROPDOWN];

export const QUESTION_TYPE_OPTION_ROW_LABEL_KEY = {
  [QUESTION_TYPES.CHECKBOXES]: 'consultationForms.addQuestionModal.optionRowLabels.checkboxes',
  [QUESTION_TYPES.RADIO_BUTTONS]: 'consultationForms.addQuestionModal.optionRowLabels.radio_buttons',
  [QUESTION_TYPES.DROPDOWN]: 'consultationForms.addQuestionModal.optionRowLabels.dropdown',
};

export const QUESTION_TYPE_OPTION_PLACEHOLDER_KEY = {
  [QUESTION_TYPES.CHECKBOXES]: 'consultationForms.addQuestionModal.optionPlaceholders.answer',
  [QUESTION_TYPES.RADIO_BUTTONS]: 'consultationForms.addQuestionModal.optionPlaceholders.answer',
  [QUESTION_TYPES.DROPDOWN]: 'consultationForms.addQuestionModal.optionPlaceholders.dropdown',
};

export const FORM_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  DRAFT: 'draft',
};

export const FORM_STATUS_LABELS = {
  [FORM_STATUS.ACTIVE]: 'consultationForms.status.active',
  [FORM_STATUS.INACTIVE]: 'consultationForms.status.inactive',
  [FORM_STATUS.DRAFT]: 'consultationForms.status.draft',
};

export const BUILDER_STEPS = [
  { number: 1, label: 'consultationForms.builder.steps.formBuilder' },
  { number: 2, label: 'consultationForms.builder.steps.setRules' },
  { number: 3, label: 'consultationForms.builder.steps.review' },
];

export const FORM_AUTOMATION_TYPES = {
  BEFORE_APPOINTMENT: 'before_appointment',
  AFTER_APPOINTMENT: 'after_appointment',
  MANUAL: 'manual',
};

export const FORM_AUTOMATION_TYPES_LABELS = {
  [FORM_AUTOMATION_TYPES.BEFORE_APPOINTMENT]: 'consultationForms.automation.beforeAppointment',
  [FORM_AUTOMATION_TYPES.AFTER_APPOINTMENT]: 'consultationForms.automation.afterAppointment',
  [FORM_AUTOMATION_TYPES.MANUAL]: 'consultationForms.automation.manual',
};

export const FORM_TEMPLATES = [
  {
    id: 'general_health',
    nameKey: 'consultationForms.templates.generalHealth.name',
    subtitleKey: 'consultationForms.templates.generalHealth.subtitle',
    questionCount: 6,
    icon: S.heartMending,
    questions: [
      { text: 'Do you have any known allergies?', type: QUESTION_TYPES.LONG_ANSWER, required: true },
      { text: 'Are you currently taking any medications?', type: QUESTION_TYPES.YES_NO, required: true },
      { text: 'Do you have any skin conditions?', type: QUESTION_TYPES.YES_NO, required: true },
      { text: 'Are you currently pregnant or breastfeeding?', type: QUESTION_TYPES.YES_NO, required: true },
      { text: 'Do you have any heart conditions?', type: QUESTION_TYPES.YES_NO, required: false },
      { text: 'Have you had any recent surgeries?', type: QUESTION_TYPES.YES_NO, required: false },
      { text: 'Do you have diabetes?', type: QUESTION_TYPES.YES_NO, required: false },
      { text: 'Any additional health information we should know?', type: QUESTION_TYPES.LONG_ANSWER, required: false },
    ],
  },
  {
    id: 'laser_treatment',
    nameKey: 'consultationForms.templates.laserTreatment.name',
    subtitleKey: 'consultationForms.templates.laserTreatment.subtitle',
    questionCount: 6,
    icon: S.writingHand,
    questions: [
      { text: 'Have you had laser treatment before?', type: QUESTION_TYPES.YES_NO, required: true },
      { text: 'Are you currently using retinoids or AHAs?', type: QUESTION_TYPES.YES_NO, required: true },
      {
        text: 'Have you been exposed to sun or used tanning beds in the last 2 weeks?',
        type: QUESTION_TYPES.YES_NO,
        required: true,
      },
      { text: 'Do you have a history of keloid scarring?', type: QUESTION_TYPES.YES_NO, required: true },
      { text: 'Are you taking any photosensitive medications?', type: QUESTION_TYPES.YES_NO, required: true },
      { text: 'Please describe your skin type', type: QUESTION_TYPES.SHORT_ANSWER, required: false },
    ],
  },
  {
    id: 'massage_therapy',
    nameKey: 'consultationForms.templates.massageTherapy.name',
    subtitleKey: 'consultationForms.templates.massageTherapy.subtitle',
    questionCount: 6,
    icon: S.heartHands,
    questions: [
      { text: 'Do you have any areas of pain or tension?', type: QUESTION_TYPES.LONG_ANSWER, required: true },
      { text: 'Do you have any injuries we should be aware of?', type: QUESTION_TYPES.YES_NO, required: true },
      {
        text: 'What pressure level do you prefer?',
        type: QUESTION_TYPES.RADIO_BUTTONS,
        required: true,
        options: ['Light', 'Medium', 'Firm', 'Deep'],
      },
      { text: 'Are you pregnant?', type: QUESTION_TYPES.YES_NO, required: true },
      { text: 'Any areas to avoid during massage?', type: QUESTION_TYPES.LONG_ANSWER, required: false },
    ],
  },
  {
    id: 'aftercare',
    nameKey: 'consultationForms.templates.aftercare.name',
    subtitleKey: 'consultationForms.templates.aftercare.subtitle',
    questionCount: 6,
    icon: S.clipboardDocument,
    questions: [
      { text: 'I understand the aftercare instructions provided', type: QUESTION_TYPES.YES_NO, required: true },
      { text: 'I agree to follow the recommended aftercare routine', type: QUESTION_TYPES.YES_NO, required: true },
      { text: 'Any concerns about aftercare?', type: QUESTION_TYPES.LONG_ANSWER, required: false },
      { text: 'Client signature', type: QUESTION_TYPES.SIGNATURE, required: true },
    ],
  },
  {
    id: 'scratch',
    nameKey: 'consultationForms.templates.scratch.name',
    subtitleKey: 'consultationForms.templates.scratch.subtitle',
    questionCount: 0,
    icon: S.plusEmoji,
    questions: [],
  },
];

export const QUESTION_LIMITS = {
  QUESTION_TEXT_MAX: 200,
  DESCRIPTION_MAX: 500,
  OPTION_TEXT_MAX: 200,
  MAX_OPTIONS_PER_QUESTION: 30,
  MAX_QUESTIONS_PER_FORM: 100,
  COUNTER_THRESHOLD: 20,
};

export const ORDERED_TYPES = [
  QUESTION_TYPES.SHORT_ANSWER,
  QUESTION_TYPES.LONG_ANSWER,
  QUESTION_TYPES.YES_NO,
  QUESTION_TYPES.CHECKBOXES,
  QUESTION_TYPES.RADIO_BUTTONS,
  QUESTION_TYPES.DROPDOWN,
  QUESTION_TYPES.SIGNATURE,
];

export const RULE_FIELD_KEYS = {
  TRIGGER_TYPE: 'trigger_type',
  TRIGGER_TIMING: 'trigger_timing',
  SERVICE_SELECTION_TYPE: 'service_selection_type',
  SERVICE_IDS: 'service_ids',
  FREQUENCY: 'frequency',
  EXPIRATION_PERIOD: 'expiration_period',
  IS_DRAFT: 'is_draft',
  IS_ACTIVE: 'is_active',
};

export const TRIGGER_TIMING = {
  ON_BOOKING_CREATION: 'on_booking_creation',
  H24_BEFORE: '24h_before',
  H48_BEFORE: '48h_before',
  WEEK_BEFORE: '1week_before',
  IMMEDIATELY: 'immediately',
  H24_AFTER: '24h_after',
  H48_AFTER: '48h_after',
  WEEK_AFTER: '1week_after',
};

export const SERVICE_SELECTION = {
  ALL: 'all',
  SELECTED: 'selected',
};

export const FORM_FREQUENCY = {
  ONCE_PER_CLIENT: 'once_per_client',
  EVERY_APPOINTMENT: 'every_appointment',
};

export const EXPIRATION_PERIOD = {
  NEVER: 'never',
  SIX_MONTHS: '6_months',
  TWELVE_MONTHS: '12_months',
  TWENTY_FOUR_MONTHS: '24_months',
};

export const TRIGGER_TIMING_OPTIONS = {
  [FORM_AUTOMATION_TYPES.BEFORE_APPOINTMENT]: [
    { value: TRIGGER_TIMING.ON_BOOKING_CREATION, labelKey: 'consultationForms.rules.timing.onBookingCreation' },
    { value: TRIGGER_TIMING.H24_BEFORE, labelKey: 'consultationForms.rules.timing.24hBefore' },
    { value: TRIGGER_TIMING.H48_BEFORE, labelKey: 'consultationForms.rules.timing.48hBefore' },
    { value: TRIGGER_TIMING.WEEK_BEFORE, labelKey: 'consultationForms.rules.timing.1weekBefore' },
  ],
  [FORM_AUTOMATION_TYPES.AFTER_APPOINTMENT]: [
    { value: TRIGGER_TIMING.IMMEDIATELY, labelKey: 'consultationForms.rules.timing.immediately' },
    { value: TRIGGER_TIMING.H24_AFTER, labelKey: 'consultationForms.rules.timing.24hAfter' },
    { value: TRIGGER_TIMING.H48_AFTER, labelKey: 'consultationForms.rules.timing.48hAfter' },
    { value: TRIGGER_TIMING.WEEK_AFTER, labelKey: 'consultationForms.rules.timing.1weekAfter' },
  ],
};

export const FREQUENCY_OPTIONS = [
  { value: FORM_FREQUENCY.ONCE_PER_CLIENT, labelKey: 'consultationForms.rules.frequency.oncePerClient' },
  { value: FORM_FREQUENCY.EVERY_APPOINTMENT, labelKey: 'consultationForms.rules.frequency.everyAppointment' },
];

export const EXPIRATION_PERIOD_OPTIONS = [
  { value: EXPIRATION_PERIOD.NEVER, labelKey: 'consultationForms.rules.expiry.never' },
  { value: EXPIRATION_PERIOD.SIX_MONTHS, labelKey: 'consultationForms.rules.expiry.6months' },
  { value: EXPIRATION_PERIOD.TWELVE_MONTHS, labelKey: 'consultationForms.rules.expiry.12months' },
  { value: EXPIRATION_PERIOD.TWENTY_FOUR_MONTHS, labelKey: 'consultationForms.rules.expiry.24months' },
];

export const RULES_DEFAULTS = {
  [RULE_FIELD_KEYS.TRIGGER_TYPE]: FORM_AUTOMATION_TYPES.BEFORE_APPOINTMENT,
  [RULE_FIELD_KEYS.TRIGGER_TIMING]: TRIGGER_TIMING.ON_BOOKING_CREATION,
  [RULE_FIELD_KEYS.SERVICE_SELECTION_TYPE]: SERVICE_SELECTION.ALL,
  [RULE_FIELD_KEYS.SERVICE_IDS]: [],
  [RULE_FIELD_KEYS.FREQUENCY]: FORM_FREQUENCY.ONCE_PER_CLIENT,
  [RULE_FIELD_KEYS.EXPIRATION_PERIOD]: EXPIRATION_PERIOD.SIX_MONTHS,
  [RULE_FIELD_KEYS.IS_DRAFT]: true,
  [RULE_FIELD_KEYS.IS_ACTIVE]: false,
};
